import { externalLaunchTokenAtom } from '@/atoms/externalLaunchAuthAtoms';
import { useKinde } from '@/hooks/useKinde';
import { getEffectiveAuthProvider } from '@/lib/auth/effectiveAuthProvider';
import { useAuthSessionMetadata } from '@/lib/auth/hooks/useAuthSessionMetadata';
import { type RootState } from '@/store';
import { isTauri } from '@/tauri/utils';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { useAuthState } from '@heidi/auth/state';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

/**
 * Re-exported so every existing importer of this specifier keeps resolving.
 * The declaration moved to `./useAuthToken` — see that file for why the token
 * read may not sit behind this module's eager store edge.
 */
export { useAuthToken } from './useAuthToken';

export interface IsAuthenticatedState {
  /** Convenience boolean — `true` when the user is authenticated. */
  isAuthenticated: boolean;
  /** Convenience boolean — `true` while the auth state is still resolving. */
  isLoading: boolean;
  /** Provider-breakdown fields preserved for backwards compat / debugging. */
  isKindeAuthenticated: boolean;
  isAuth0Authenticated: boolean;
  isAuth0DesktopAuthenticated: boolean;
}

/**
 * Unified authentication-status hook.
 *
 * For every provider except Kinde-web and FHIR, this is backed by the
 * cache atoms — the cache populates `authStateAtom`, this hook reads
 * from it. Two carve-outs bypass the cache:
 *
 * - **Kinde-web**: the cache's Kinde-web branch is stubbed (returns
 *   `null`) until the `/api/kinde-session` endpoint sibling ships;
 *   routing Kinde-web through the cache prematurely would drive every
 *   Kinde-web user to `unauthenticated` the moment the cache wires up.
 *   Reads `useKinde()` directly.
 *
 * - **FHIR**: FHIR sessions are launched externally with a JWT stored
 *   in `externalLaunchTokenAtom`, or in `state.auth.token`
 *   after a reload (the external-launch token is never persisted,
 *   so it's cleared on reload; `auth.token` is persisted
 *   and rehydrates the FHIR JWT). The cache's FHIR branch in
 *   `readFromProviderSourceOfTruth` returns `null` (FHIR tokens
 *   aren't refreshable via a provider source-of-truth), so
 *   `authStateAtom` would stay at `'unauthenticated'` and lock out
 *   legitimately-launched FHIR consumers. This hook carries the FHIR
 *   presence-gate on its own — the axios/interceptor half of the
 *   gate lands with PR D1's external-launch wiring (today the
 *   interceptor reads only `auth.token` and no code path writes the
 *   external-launch token).
 *
 * The public return shape is preserved across all branches so the 10+
 * existing destructuring consumers keep working without modification.
 *
 * @example
 * ```tsx
 * const { isAuthenticated, isLoading } = useIsAuthenticated();
 * ```
 */
export const useIsAuthenticated = (): IsAuthenticatedState => {
  // Hooks must run unconditionally per the Rules of Hooks; we call
  // every underlying hook on every render and then choose which to
  // return based on the active provider.
  const state = useAuthState();
  const session = useAuthSessionMetadata();
  const kindeAuth = useKinde();

  // Subscribe to Redux `authProvider` so this hook re-renders when it
  // changes — even if the atoms don't move in the same tick. Without
  // the subscription, a Redux-only flip (desktop provider migration,
  // KeycloakReduxSync's _setAuthProvider dispatch before the next cache
  // fetch resolves) would leave the gate and provider-breakdown stale
  // until something else forces a re-render.
  const reduxProvider = useSelector((s: RootState) => s.auth.authProvider);

  // FHIR consumers keep their JWT in the external-launch token (fresh launch)
  // or auth.token (post-reload rehydration; the external-launch token is never
  // persisted). Subscribe to both so the hook re-renders
  // when either changes. Used only by the FHIR bypass below.
  const externalLaunchToken = useAtomValue(externalLaunchTokenAtom);
  const legacyAuthToken = useSelector((s: RootState) => s.auth.token);
  // Hoisted so every branch below (Kinde-web, SDK-init carve-out, the
  // generic cache-backed branch) can treat a persisted token as
  // provisionally-authenticated while its own settle signal is still
  // pending — see each branch for its specific rationale.
  const hasPersistedToken = !!legacyAuthToken;

  // The transport layer already returns this token directly for
  // external-launch flows (FHIR, Medical Director), so this gate agrees
  // with what requests actually do. Must run before the provider branches
  // below — an MD session can carry provider KINDE, and the Kinde-web
  // branch would otherwise report logged-out first.
  if (externalLaunchToken) {
    return {
      isAuthenticated: true,
      isLoading: false,
      isKindeAuthenticated: false,
      isAuth0Authenticated: false,
      isAuth0DesktopAuthenticated: false,
    };
  }

  // Effective provider — cookie-first on web so the migration window
  // (cookie=AUTH0, Redux=KINDE) stops taking the Kinde fallback as
  // soon as the cookie flips. Without this, useIsAuthenticated would
  // keep reporting Kinde state while the cache already holds Auth0.
  // `getEffectiveAuthProvider()` reads the cookie and Redux internally;
  // re-runs cleanly on each render now that the subscription above
  // forces a re-render on the Redux side. Cookie reactivity is a
  // separate concern.
  const effectiveProvider = getEffectiveAuthProvider();
  const isKindeWebLegacy = !isTauri() && effectiveProvider === AuthProvider.KINDE;

  if (isKindeWebLegacy) {
    const kindeIsAuthenticated = kindeAuth.isAuthenticated ?? false;
    // The Kinde SDK's own `isLoading` is a real network round-trip
    // (`fetch('/api/auth/setup')`) on every page load, not an instant
    // local read — so without this carve-out, a Kinde-web user with a
    // persisted redux token (the common case) flashes signed-out UI on
    // every load in any consumer converted to `!!token -> isAuthenticated`
    // parity. Mirrors the generic branch's provisional read below: the
    // SDK's own settled verdict (`kindeIsAuthenticated`) always overrides
    // once it lands, so a genuinely logged-out visitor still resolves to
    // `false` once the SDK settles (NOT stuck provisionally-authenticated).
    const kindeIsLoading = kindeAuth.isLoading ?? false;
    // The transport reads the redux mirror for Kinde, so authenticated
    // requires the mirrored token; SDK-authenticated-but-unmirrored is a
    // loading state (credential in transit), not signed-out.
    return {
      isAuthenticated: hasPersistedToken && (kindeIsAuthenticated || kindeIsLoading),
      isLoading: !hasPersistedToken && (kindeIsLoading || kindeIsAuthenticated),
      isKindeAuthenticated: hasPersistedToken && kindeIsAuthenticated,
      isAuth0Authenticated: false,
      isAuth0DesktopAuthenticated: false,
    };
  }

  // Kinde-web SDK-init window (fresh browser / redirect-back from
  // Kinde hosted login). Neither the `heidi-auth-provider` cookie
  // nor `state.auth.authProvider` is populated yet, so
  // `effectiveProvider === null`. The scheduler's permanent Kinde-web
  // skip hasn't engaged yet here — it gates on `effectiveProvider ===
  // KINDE`, not `null` — so the mount-time prewarm still fires, and
  // the cache's null-provider branch bare-nulls unconditionally on
  // web, flipping `authStateAtom` to `'unauthenticated'` before the
  // Kinde SDK itself has settled → without a carve-out consumers
  // observe `{isAuthenticated: false, isLoading: false}` for the
  // whole SDK-init window and flash SignInModal (`useOpenApiLaunch`).
  // This branch reports `isLoading: true` here while the Kinde SDK is
  // still initialising, UNLESS a persisted token already makes this
  // provisionally-authenticated (same parity
  // rule as the Kinde-web branch above and the generic branch below)
  // — a token-holder shouldn't flash signed-out just because the SDK
  // hasn't confirmed the session yet. Guard: once `kindeAuth.isLoading`
  // clears to false the branch drops through, so a settled logged-out
  // visitor still resolves to `{isAuthenticated: false, isLoading:
  // false}` (NOT perpetually loading or authenticated). Web-only —
  // desktop takes the Redux path in `getEffectiveAuthProvider` and
  // null-provider there is handled by the dispatcher's transient gate.
  if (!isTauri() && effectiveProvider === null && kindeAuth.isLoading) {
    if (hasPersistedToken) {
      return {
        isAuthenticated: true,
        isLoading: false,
        isKindeAuthenticated: false,
        isAuth0Authenticated: false,
        isAuth0DesktopAuthenticated: false,
      };
    }
    return {
      isAuthenticated: false,
      isLoading: true,
      isKindeAuthenticated: false,
      isAuth0Authenticated: false,
      isAuth0DesktopAuthenticated: false,
    };
  }

  // FHIR bypass — cache's FHIR branch returns null, so authStateAtom
  // can't be used to gate FHIR-launched consumers. This hook carries
  // the FHIR presence-gate alone: the external-launch token first
  // (fresh launch), `auth.token` as the reload fallback
  // (the external-launch token is never persisted, but
  // `auth.token` is). The axios/interceptor half of this gate lands
  // with PR D1's external-launch wiring — today the interceptor
  // reads only `auth.token`. The launch flow is synchronous once the
  // JWT lands in state, so isLoading is always false here — either a
  // token is present (authenticated) or it isn't (unauthenticated).
  const isFhir = effectiveProvider === AuthProvider.FHIR;
  if (isFhir) {
    const hasFhirToken = !!(externalLaunchToken || legacyAuthToken);
    return {
      isAuthenticated: hasFhirToken,
      isLoading: false,
      isKindeAuthenticated: false,
      isAuth0Authenticated: false,
      isAuth0DesktopAuthenticated: false,
    };
  }

  // While the cache is still resolving, a persisted token is treated as
  // authenticated so consumers see the same boot behavior as the raw token
  // gate; the cache's settled state always overrides. This leans on
  // `auth.token` and becomes a no-op once that slot is removed.
  const isAuthenticated =
    state === 'authenticated' || (state === 'loading' && hasPersistedToken);
  const isLoading = state === 'loading' && !hasPersistedToken;

  // Provider-breakdown fields describe which provider's session is
  // currently live per the cache. Use effective provider (cookie-first
  // on web) so the breakdown matches what the cache actually routed
  // through. The `?? reduxProvider` is strictly redundant today —
  // getEffectiveAuthProvider already falls back to Redux internally —
  // but kept as a defensive narrowing in case the helper's null
  // semantics ever change (e.g., a future "ambiguous" return where the
  // helper declines to pick between cookie and Redux). Also makes the
  // dependency on the subscribed `reduxProvider` value-explicit at this
  // call site.
  const activeProvider = effectiveProvider ?? reduxProvider;
  const authProviderMatches = (target: AuthProvider): boolean =>
    isAuthenticated && session !== null && activeProvider === target;

  return {
    isAuthenticated,
    isLoading,
    isKindeAuthenticated: authProviderMatches(AuthProvider.KINDE),
    isAuth0Authenticated: authProviderMatches(AuthProvider.AUTH0),
    isAuth0DesktopAuthenticated: authProviderMatches(AuthProvider.AUTH0_DESKTOP),
  };
};
